/* Copyright (c) 2020-present CloudIO Inc */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEvent, useLatest } from '@cloudio-saas/statex';
import { useEffect, useRef } from 'react';

export default function useDebounce(
  callback: (...args: any[]) => void,
): [
  (timeout: number, ...args: any[]) => void,
  () => void,
  (...args: any[]) => void,
] {
  const requestRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useLatest(callback);
  const argsRef = useRef<any[]>([]);

  useEffect(() => {
    return () => {
      if (requestRef.current) {
        clearTimeout(requestRef.current);
        requestRef.current = null;
      }
    };
  }, []);

  const call = useEvent(() => {
    requestRef.current = null;
    callbackRef.current(...argsRef.current);
  });

  const callImmediate = useEvent((...args: any[]) => {
    if (requestRef.current) {
      clearTimeout(requestRef.current);
      requestRef.current = null;
    }
    callbackRef.current(...args);
  });

  const debounce = useEvent((timeout: number, ...args: any[]) => {
    argsRef.current = args;
    if (requestRef.current) {
      clearTimeout(requestRef.current);
    }
    requestRef.current = setTimeout(call, timeout);
  });

  const cancelDebounce = useEvent(() => {
    if (requestRef.current) {
      clearTimeout(requestRef.current);
      requestRef.current = null;
    }
  });

  return [debounce, cancelDebounce, callImmediate];
}
